import type { RecipientRole, UploadResult } from './api'
import type { Candidate } from './placement'

// The routing rule and the turns of a document sent one after another, as the
// sending page builds them. Their own module, with no React in it, so the app
// runs these in place (mobile/src/shared/web.ts) instead of keeping copies
// that can drift, as it does newEnvelopeDraft.ts. The server's
// TestTheThresholdRuleIsTheWebsRule finds isRoutingThreshold wherever it is
// under web/src and holds its pattern to the server's, so keep its form:
// `export function isRoutingThreshold(value: string): boolean`, returning a
// `/pattern/.test(s)`.

/**
 * Each row's routing order, as the envelope is sent.
 *
 * In any order, everybody shares one. One after another, each row is its own
 * turn, except where a routing rule chooses between three or more signers:
 * everybody after the signer the rule runs after then shares the next turn,
 * so the rule has run before any of them can sign, and whoever it does not
 * skip signs next.
 *
 * That turn is counted from the rule's signer, not from the top of the list.
 * Counted from the top, a viewer or a copy row above the first signer put
 * the rule's signer in the same turn as the signers it chooses between, and
 * the server refuses a rule that can skip somebody who may already have
 * signed. And it is shared only when a rule is actually sent: a routing box
 * still ticked after the first signer's Text box was removed sends no rule,
 * and then sharing a turn would have let two signers sign at once on an
 * envelope the page calls one after another.
 *
 * @param ruleAfter the row of the signer the rule runs after, or null when
 *   no rule is sent.
 */
export function routingOrders(
  roles: RecipientRole[], sequential: boolean, ruleAfter: number | null,
): number[] {
  const signers = roles.filter((role) => role === 'signer').length
  return roles.map((_, i) => {
    if (!sequential) return 1
    if (ruleAfter !== null && signers >= 3 && i > ruleAfter) return ruleAfter + 2
    return i + 1
  })
}

/**
 * Whether the server can read a routing threshold as a number, read exactly
 * as it reads one (internal/routing parseNumber): spaces around it, one "$"
 * in front and commas anywhere are dropped, and what is left is a finite
 * decimal number. "50,000" and "$50000.50" are numbers; "50k", "50 000",
 * "fifty thousand" and an empty box are not, and the server refuses the send
 * with any of them. Checked here so "Create signing links" stays off and
 * says why, rather than the sender pressing it and being told.
 */
export function isRoutingThreshold(value: string): boolean {
  const s = value.trim().replace(/^\$/, '').replaceAll(',', '')
  return /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(s) && Number.isFinite(Number(s))
}

/**
 * The rows a routing rule chooses between: every signer after the one it runs
 * after. Over the threshold it skips the second of them, under it the first.
 */
export function routingBranch(roles: RecipientRole[], ruleAfter: number): number[] {
  return roles.flatMap((role, i) => (i > ruleAfter && role === 'signer' ? [i] : []))
}

/**
 * What the rule does, in the names of the people it skips.
 *
 * The rows are the ones the rule is sent with. The sentence named the second
 * and third rows whoever they were, so with somebody viewing first it named
 * the rule's own signer and a signer it never skips.
 */
export function routingOutcome(names: string[], branch: number[]): string {
  const who = (i: number) => names[i]?.trim() || `signer ${i + 1}`
  if (branch.length === 0) return ''
  return branch.length >= 2
    ? `Over the limit → skip ${who(branch[1])}. Under it → skip ${who(branch[0])}.`
    : `Over the limit → ${who(branch[0])} must sign. Under it, they are skipped.`
}

/**
 * The places the envelope's documents ask to be filled in, each with its
 * document's position.
 *
 * A filled-in value has to be drawable on every document that carries one,
 * so one old PDF in the envelope means signature boxes only, and the server
 * refuses the send otherwise. Each document's own reading knows only its own
 * PDF: a current one beside an old one offered text and date boxes that, once
 * accepted, made the envelope one nobody could send.
 */
export function envelopeCandidates(
  docs: { upload: Pick<UploadResult, 'document_key' | 'supports_fields'> }[],
  readings: Record<string, { candidates: Candidate[] }>,
): Candidate[] {
  const supportsFields = docs.every((d) => d.upload.supports_fields)
  return docs.flatMap((d, i) => (readings[d.upload.document_key]?.candidates ?? [])
    .filter((c) => supportsFields || c.kind === 'signature')
    .map((c) => ({ ...c, documentIndex: i })))
}

/**
 * What is missing before a routing rule can be set, or '' when nothing is.
 * A rule runs after the first signer, on a number they type, so it needs the
 * signers one after another, a second signer to choose, and a Text box on the
 * first signer.
 */
export function routingHint(o: { sequential: boolean; signers: number; hasText: boolean }): string {
  if (!o.sequential) return 'Switch signing order to one after another.'
  if (o.signers < 2) return 'Add another signer (two signers minimum).'
  if (!o.hasText) return 'Add a Text field for the first signer. They enter the amount the rule checks.'
  return ''
}

/** "1st", "2nd", "3rd", "4th", "11th", "22nd". */
export function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'
  return `${n}${suffix}`
}

/** One row of the roster as it was sent: whom it names, and what they do. */
export interface SentRow {
  name: string
  role: RecipientRole
}

/** The routing rule as it was sent, by row: the signer it runs after, and the rows it chooses between (routingBranch). */
export interface SentRule {
  after: number
  branch: number[]
}

/**
 * What the page after a one-after-another send says about the turns: a lead
 * sentence, and a label for each signer's row ('' for every other row).
 *
 * Read from the routing orders actually sent (routingOrders), not from the
 * rows. A turn is a place in the line: every distinct order of somebody who
 * signs, witnesses or views is one, and a copy recipient, who is sent a copy
 * when everyone has finished, holds none. Counting rows said "Signs 3rd" and
 * "Signs 4th" for the two signers a rule chooses between, who share one turn,
 * and "only once everyone above them has" for two people who do not wait for
 * each other, and the lead's "after everyone above them" was false for the
 * two who share a turn (L13). The lead names the first person by what they do: a viewer
 * first is not "goes first" to sign.
 */
export function sentTurnWords(
  rows: SentRow[],
  orders: number[],
  rule: SentRule | null,
): { lead: string; labels: string[] } {
  const inLine = rows.flatMap((r, i) => (r.role === 'cc' ? [] : [i]))
  const turns = [...new Set(inLine.map((i) => orders[i]))].sort((a, b) => a - b)
  const turnOf = (i: number) => turns.indexOf(orders[i]) + 1
  const who = (i: number) => rows[i]?.name.trim() || `signer ${i + 1}`
  const labels = rows.map((r, i) => {
    if (r.role !== 'signer') return ''
    const base = `Signs ${ordinal(turnOf(i))}`
    if (!rule || !rule.branch.includes(i)) return base
    // Over the limit the rule skips branch[1]; under it, branch[0]. With one
    // row to choose, that row signs only over the limit.
    if (rule.branch.length === 1 || i === rule.branch[0]) return `${base} if the amount is over the limit`
    if (i === rule.branch[1]) return `${base} if the amount is not over the limit`
    return base
  })
  const first = inLine.reduce<number | null>((f, i) => (f === null || orders[i] < orders[f] ? i : f), null)
  const does: Record<RecipientRole, string> = {
    signer: 'signs first', needs_to_view: 'views it first', witness: 'witnesses first', cc: '',
  }
  let lead = 'Each person gets their own link and signs in turn'
  lead += first === null ? '.' : `, so ${who(first)} ${does[rows[first].role]}.`
  if (rule && rule.branch.length >= 2) {
    lead += ` After ${who(rule.after)} signs, the routing rule chooses between ${who(rule.branch[0])} and ${who(rule.branch[1])}.`
  } else if (rule && rule.branch.length === 1) {
    lead += ` After ${who(rule.after)} signs, the routing rule decides whether ${who(rule.branch[0])} signs.`
  }
  return { lead, labels }
}

/**
 * The routing box as Send again sets it from the rule an envelope was sent
 * with: on, with its threshold and the first signer's Text box it reads, or
 * off. `lost` says the envelope had a rule this page cannot set up again (the
 * page builds one shape: after the first signer, over a number, choosing as
 * routingBranch does), so the page can say so rather than send without it.
 *
 * Send again brought the people and every box back and dropped the rule, so
 * a document that skipped an approver under the limit went out again asking
 * everybody, one after another, with nothing on screen to say a rule was gone.
 *
 * @param parties each party in the order the page lists them, with the
 *   participations it was and its boxes in the page's order.
 */
export function resentRouting(
  parties: { role: RecipientRole; participation_ids: string[]; fields: { id: string; kind: string }[] }[],
  rules: { after_participation_id: string; condition: { field_id: string; op: string; value?: string };
    if_true_skip: string[]; if_false_skip: string[] }[] | undefined,
): { enabled: true; threshold: string; fieldIndex: number } | { enabled: false; lost: boolean } {
  if (!rules?.length) return { enabled: false, lost: false }
  const lost = { enabled: false as const, lost: true }
  if (rules.length !== 1) return lost
  const [rule] = rules
  const partyOf = (id: string) => parties.findIndex((p) => p.participation_ids.includes(id))
  const roles = parties.map((p) => p.role)
  const trigger = roles.indexOf('signer')
  if (trigger < 0 || partyOf(rule.after_participation_id) !== trigger || rule.condition.op !== 'gt') return lost
  const fieldIndex = parties[trigger].fields.findIndex((f) => f.id === rule.condition.field_id)
  if (fieldIndex < 0 || parties[trigger].fields[fieldIndex].kind !== 'text') return lost
  const skips = (ids: string[]) => [...new Set(ids.map(partyOf))].sort((a, b) => a - b)
  const branch = routingBranch(roles, trigger)
  const same = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i])
  const built = branch.length === 1
    ? { yes: [] as number[], no: [branch[0]] }
    : branch.length >= 2 ? { yes: [branch[1]], no: [branch[0]] } : null
  if (!built || !same(skips(rule.if_true_skip), built.yes) || !same(skips(rule.if_false_skip), built.no)) return lost
  return { enabled: true, threshold: rule.condition.value ?? '', fieldIndex }
}

/**
 * Each created recipient's row in the send. The server answers one recipient
 * per participation, in the order sent, and a signing group is one row whose
 * members are each a participation.
 */
export function sentRowOf(rows: { members?: unknown[] }[]): number[] {
  return rows.flatMap((r, i) => (r.members?.length ? r.members.map(() => i) : [i]))
}

